import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parseNumericInput } from '../utils/numberFormat.js'
import { generateRequestId } from '../utils/requestId.js'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { showConfirm, showError } from '../utils/notifications.js'
import { handleHotKeyboardShortcut } from '../utils/hotKeyboardShortcuts.js'
import usePasteOverflow from '../hooks/usePasteOverflow.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

const DEBOUNCE_DELAY = 800


const parseRateInput = parseNumericInput

function formatRate(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return `${n.toFixed(2)}%`
}

function resizeRates(rates, yearCount) {
  const result = {}
  let lastKnown = 0
  for (let y = 1; y <= yearCount; y++) {
    const key = String(y)
    if (rates[key] !== undefined && rates[key] !== null) {
      lastKnown = Number(rates[key])
    }
    result[key] = lastKnown
  }
  return result
}

export default function AssumptionGrowthRatesTable({
  valuationGroupId, indexUrl, batchUpdateUrl, batchCreateUrl, createRowUrl, destroyRowUrl, resetUrl,
  csrfToken, readOnly = false, valuationDate, yearCount, translations = {}
}) {
  const hotRef = useRef(null)
  const pendingRef = useRef(new Map())
  const flushTimeoutRef = useRef(null)
  const [rawData, setRawData] = useState([])
  const [loading, setLoading] = useState(true)
  // EVA-147: which period rows the user has UNticked. Rows default to selected
  // (checked), so a fresh load / new row pushes everything as before. Tracking
  // the *unchecked* set means new rows are checked automatically and survivors
  // of a deletion leave only harmless stale ids behind.
  const [uncheckedIds, setUncheckedIds] = useState(() => new Set())

  // Bridge to the push modal: the Stimulus push controller reads this at push
  // time to send only the ticked growth_rate_ids. Undefined → push everything.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    window.__evaSelectedGrowthRateIds = () =>
      rawData.filter((r) => r.id != null && !uncheckedIds.has(r.id)).map((r) => r.id)
    return () => { delete window.__evaSelectedGrowthRateIds }
  }, [rawData, uncheckedIds])

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await fetch(indexUrl, {
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken }
      })
      const json = await res.json()
      if (json.success && json.data) {
        setRawData(json.data)
      }
    } catch (e) {
      console.error('Failed to fetch growth rate assumptions:', e)
    } finally {
      setLoading(false)
    }
  }, [indexUrl, csrfToken])

  useEffect(() => { fetchData() }, [fetchData])

  // EVA-138: paste overflow — create missing rows server-side, then replay.
  // Callbacks reference flushChanges lazily (defined below); they only run
  // after the render completes, so no TDZ issue.
  const { handleBeforePaste } = usePasteOverflow({
    hotRef,
    enabled: !readOnly,
    createRows: async (count) => {
      const res = await fetch(batchCreateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          request_id: generateRequestId(),
          items: Array.from({ length: count }, (_, i) => ({ temp_id: `paste_${Date.now()}_${i}`, data: {} }))
        })
      })
      const json = await res.json()
      return json.success === true
    },
    refetchData: () => fetchData(true), // silent — non-silent fetch unmounts HotTable
    flushNow: () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
      return flushChanges()
    },
    getAnchorId: (row) => rawData[row]?.id,
    resolveAnchorRow: (id) => {
      const idx = rawData.findIndex(r => r.id === id)
      return idx >= 0 ? idx : undefined
    }
  })

  // Listen for "Add Row" button click
  useEffect(() => {
    if (readOnly) return
    const handler = async () => {
      try {
        const res = await fetch(createRowUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ title: 'New Rate', request_id: generateRequestId() })
        })
        const json = await res.json()
        if (json.success) {
          await fetchData(true)
          requestAnimationFrame(() => {
            setTimeout(() => {
              const hot = hotRef.current?.hotInstance
              if (!hot) return
              const lastRow = hot.countRows() - 1
              hot.selectCell(lastRow, 1) // title column (col 0 is the push checkbox)
              hot.getActiveEditor()?.beginEditing()
            }, 50)
          })
        }
      } catch (e) {
        console.error('Failed to create row:', e)
      }
    }
    window.addEventListener('addGrowthRateRow', handler)
    return () => window.removeEventListener('addGrowthRateRow', handler)
  }, [readOnly, createRowUrl, csrfToken, fetchData])

  // Listen for "Reset to Defaults" button click
  useEffect(() => {
    if (readOnly || !resetUrl) return
    const handler = async () => {
      const confirmed = await showConfirm({
        title: translations.confirmResetTitle,
        message: translations.confirmResetMessage,
        confirmText: translations.confirmResetConfirm,
        cancelText: translations.confirmCancel
      })
      if (!confirmed) return
      try {
        const res = await fetch(resetUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken
          }
        })
        const json = await res.json()
        if (json.success) {
          pendingRef.current = new Map()
          if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
          await fetchData(true)
        } else {
          showError(json.error || 'Reset failed')
        }
      } catch (e) {
        console.error('Failed to reset growth rates:', e)
      }
    }
    window.addEventListener('resetGrowthRates', handler)
    return () => window.removeEventListener('resetGrowthRates', handler)
  }, [readOnly, resetUrl, csrfToken, fetchData])

  // Build table data from raw records.
  // Col 0 = push checkbox (EVA-147), col 1 = title, cols 2..N+1 = year rates.
  const tableData = useMemo(() => {
    return rawData.map((row) => {
      const rates = resizeRates(row.rates || {}, yearCount)
      const cells = [ row.id != null && !uncheckedIds.has(row.id), row.title ]
      for (let y = 1; y <= yearCount; y++) {
        cells.push(rates[String(y)] ?? 0)
      }
      return cells
    })
  }, [rawData, yearCount, uncheckedIds])

  // Flush pending changes (pendingRef is keyed by record id, not row index,
  // so a refetch/reorder between edit and flush can't misroute the update)
  const flushChanges = useCallback(async () => {
    const pending = pendingRef.current
    if (pending.size === 0) return

    const items = []
    for (const [recordId, changedCols] of pending.entries()) {
      const changes = {}
      const ratesUpdate = {}
      for (const [col, value] of changedCols.entries()) {
        if (col === 0) {
          changes.title = value
        } else {
          ratesUpdate[String(col)] = value
        }
      }
      if (Object.keys(ratesUpdate).length > 0) {
        changes.rates = ratesUpdate
      }
      items.push({ id: recordId, changes })
    }
    pendingRef.current = new Map()

    if (items.length === 0) return

    try {
      const res = await fetch(batchUpdateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ items, request_id: generateRequestId() })
      })
      const json = await res.json()
      if (!json.success) {
        // The whole batch rolled back server-side — resync the grid
        showError('Failed to save changes — please check the entered values')
        fetchData(true)
      }
    } catch (e) {
      console.error('Failed to batch update:', e)
      showError('Failed to save changes')
      fetchData(true)
    }
  }, [batchUpdateUrl, csrfToken, fetchData])

  // Debounced flush
  const scheduledFlush = useCallback(() => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushTimeoutRef.current = setTimeout(() => flushChanges(), DEBOUNCE_DELAY)
  }, [flushChanges])

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
      flushChanges()
    }
  }, [flushChanges])

  // Delete row handler
  const handleDeleteRow = useCallback(async (rowIndex) => {
    const record = rawData[rowIndex]
    if (!record || record.default_row) return
    const deleteMessage = (translations.confirmDeleteMessage || 'Delete ":title"? This cannot be undone.')
      .replace(':title', record.title)
    const confirmed = await showConfirm({
      title: translations.confirmDeleteTitle,
      message: deleteMessage,
      confirmText: translations.confirmDeleteConfirm,
      cancelText: translations.confirmCancel
    })
    if (!confirmed) return

    try {
      const res = await fetch(destroyRowUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ id: record.id, request_id: generateRequestId() })
      })
      const json = await res.json()
      if (json.success) {
        fetchData(true)
      }
    } catch (e) {
      console.error('Failed to delete row:', e)
    }
  }, [rawData, destroyRowUrl, csrfToken, fetchData])

  const hotSettings = useMemo(() => ({
    data: tableData,
    rowHeaders: false,
    colHeaders: true,
    fixedColumnsStart: 2,
    themeName: 'ht-theme-horizon',
    licenseKey: HOT_LICENSE_KEY,
    sanitizer: sanitizeHandsontableHtml,
    stretchH: 'all',
    width: '100%',
    height: 'auto',
    manualColumnResize: true,
    contextMenu: readOnly ? false : {
      items: {
        delete_row_custom: {
          name: 'Delete row',
          disabled() {
            const selected = this.getSelectedRangeLast()
            if (!selected) return true
            const row = selected.from.row
            const record = rawData[row]
            return !record || record.default_row
          },
          callback(_key, selection) {
            const row = selection[0]?.start?.row
            if (row !== undefined) handleDeleteRow(row)
          }
        }
      }
    },
    afterGetColHeader(col, th) {
      const holder = th.querySelector('.colHeader')
      if (!holder) return

      if (col === 0) {
        holder.textContent = translations.pushColumn || 'Push'
        th.classList.add('htCenter')
        return
      }

      if (col === 1) {
        holder.textContent = 'Category'
        return
      }

      th.classList.add('htRight')

      // Year columns are shifted by the checkbox + category columns.
      const yearIndex = col - 1
      if (valuationDate) {
        const d = new Date(valuationDate)
        const year = d.getFullYear() + yearIndex
        holder.textContent = String(year)
      } else {
        holder.textContent = `Year ${yearIndex}`
      }
    },
    cells(row, col) {
      const cellProps = {}

      // Col 0: per-row push checkbox (EVA-147). Toggleable for every row —
      // default rows can be excluded from a push too — disabled only read-only.
      if (col === 0) {
        cellProps.type = 'checkbox'
        cellProps.className = 'htCenter htMiddle'
        cellProps.readOnly = readOnly
        return cellProps
      }

      if (col === 1) {
        const record = rawData[row]
        cellProps.readOnly = readOnly || !record || record.default_row
        return cellProps
      }

      if (readOnly) {
        cellProps.readOnly = true
        return cellProps
      }

      cellProps.renderer = function(instance, td, rowIdx, colIdx, prop, value, cellProperties) {
        td.textContent = formatRate(value)
        td.style.textAlign = 'right'
        if (cellProperties.readOnly) {
          td.style.color = '#999'
        }
      }

      return cellProps
    },
    beforePaste: handleBeforePaste,
    afterChange(changes, source) {
      if (!changes || source === 'loadData' || source === 'server' || readOnly) return

      const hot = hotRef.current?.hotInstance

      // Col 0: push checkbox — transient selection, never persisted (EVA-147).
      const checkboxChanges = changes.filter(([, col]) => col === 0)
      if (checkboxChanges.length > 0) {
        setUncheckedIds((prev) => {
          const next = new Set(prev)
          checkboxChanges.forEach(([row, , , newVal]) => {
            const record = rawData[row]
            if (!record || record.id == null) return
            if (newVal) next.delete(record.id)
            else next.add(record.id)
          })
          return next
        })
      }

      const dataChanges = changes.filter(([, col]) => typeof col === 'number' && col >= 1)
      if (dataChanges.length === 0) return

      setRawData((prev) => {
        const next = prev.map((r) => ({ ...r, rates: { ...r.rates } }))

        dataChanges.forEach(([row, col, oldVal, newVal]) => {
          const record = next[row]
          if (!record) return

          // Title column (col 1 after the checkbox column)
          if (col === 1) {
            const trimmed = (newVal || '').trim()
            if (!trimmed) {
              if (hot) hot.setDataAtCell(row, 1, oldVal, 'server')
              return
            }
            record.title = trimmed
            if (!pendingRef.current.has(record.id)) {
              pendingRef.current.set(record.id, new Map())
            }
            pendingRef.current.get(record.id).set(0, trimmed)
            return
          }

          const isEmpty = newVal === '' || newVal === null || newVal === undefined
          const parsed = isEmpty ? 0 : (typeof newVal === 'number' ? newVal : parseRateInput(newVal))
          if (parsed === null) return

          // Year columns are shifted by the checkbox + category columns; the
          // pending buffer is keyed by year number (1..N), not grid column.
          const year = col - 1
          for (let y = year; y <= yearCount; y++) {
            record.rates[String(y)] = parsed
          }

          if (!pendingRef.current.has(record.id)) {
            pendingRef.current.set(record.id, new Map())
          }
          const colMap = pendingRef.current.get(record.id)
          for (let y = year; y <= yearCount; y++) {
            colMap.set(y, parsed)
          }
        })

        return next
      })

      scheduledFlush()
    },
    afterDocumentKeyDown(event) {
      if (readOnly) return
      const hot = hotRef.current?.hotInstance
      handleHotKeyboardShortcut(event, {
        onAddRow: () => window.dispatchEvent(new CustomEvent('addGrowthRateRow')),
        onDeleteRow: () => {
          if (!hot) return
          const selected = hot.getSelectedRangeLast()
          if (!selected) return
          const row = selected.from.row
          const record = rawData[row]
          if (record && !record.default_row) handleDeleteRow(row)
        }
      })
    }
  }), [tableData, readOnly, rawData, yearCount, valuationDate, handleDeleteRow, scheduledFlush, handleBeforePaste])

  // Expose the Handsontable instance for end-to-end specs and console debugging
  useEffect(() => {
    if (typeof window !== 'undefined' && !loading) {
      window.__evaAssumptionGrowthRatesHot = hotRef.current?.hotInstance
    }
    return () => {
      if (typeof window !== 'undefined') delete window.__evaAssumptionGrowthRatesHot
    }
  })

  return (
    <div className="growth-rates-table-container">
      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <HotTable ref={hotRef} {...hotSettings} />
      )}
    </div>
  )
}
